/**
 * SEO component — drop into any page to manage meta tags, OG/Twitter cards,
 * canonical URLs and JSON-LD structured data via react-helmet-async.
 *
 * Usage:
 *   <SEO
 *     title="Page Title"
 *     description="Meta description, 140–160 chars."
 *     path="/some-path"
 *     image="/img/og/some-image.jpg"
 *     keywords={["china travel","..."]}
 *     jsonLd={{ "@context":"https://schema.org", ... }}
 *   />
 */
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://thechinaexplorer.com";
const DEFAULT_OG_IMAGE = "/img/og/og-default.jpg";
const BRAND = "The China Explorer";

const SEO = ({
  title,
  description,
  path = "/",
  image,
  keywords = [],
  type = "website",
  jsonLd,
  noindex = false,
  locale = "en_US",
  publishedTime,
  modifiedTime,
  author = "The China Explorer Editorial",
}) => {
  const fullTitle = title ? `${title} | ${BRAND}` : `${BRAND} — Discover Five Thousand Years of Wonder`;
  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
  const ogImg = image
    ? (image.startsWith("http") ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}${DEFAULT_OG_IMAGE}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
      <meta name="author" content={author} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImg} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImg} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export const SITE = {
  url: SITE_URL,
  brand: BRAND,
  defaultOgImage: DEFAULT_OG_IMAGE,
};

export default SEO;
